/**
 * ConnectorContainersList — per-connector visibility panel for the Connections
 * surface (spec §6). Shows each classified container (Slack channel / Linear
 * team / Notion space / GitHub repo) with a privacy-class chip and its
 * effective access, so an admin can see at a glance which sources are dark and
 * who they reach. Admin-only on the server (GET /api/connections/:source/containers
 * 403s for non-admins); this component quietly renders nothing on 403.
 *
 * Presentation: collapsed by default behind a summary line ("14 channels · 11
 * open to everyone · 3 private") so a chatty workspace doesn't stretch the
 * Connections page; the expanded list spans the service card's full width.
 * "Container" is a backend term — the heading uses the connector's own noun
 * (channels / repositories / teams / spaces).
 *
 * Slack DMs group under "Direct messages": they are permanently private (the
 * PATCH refuses a workspace override for them), so they render without the
 * open-to-workspace control.
 *
 * Admin reclassification (YAG-263): a private/shared container can be opened to
 * the whole workspace (access override) or reverted, one tap each — the PATCH
 * re-stamps the container's items both directions. Reversible, so no confirm
 * modal (confirm-only-for-real-side-effects). This list only ever renders for
 * admins (the GET 403s otherwise), so the control is always authorized here.
 * Warm/light identity, no new fonts, Tailwind v4 var utilities.
 */
import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Badge } from "../ui/badge.js";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible.js";
import { PRIVACY_BADGE, type ContainerPrivacyClass } from "../ui/containerPrivacy.js";
import { captureUiError } from "../sentry.js";
import { fetchConnectionTeams } from "../teams/teamsApi.js";
import { readJsonError } from "../lib/readJsonError.js";

interface ContainerOwner {
  userId: string;
  displayName: string | null;
  email: string | null;
}

interface ConnectorContainer {
  id: string;
  containerKind: string;
  externalId: string;
  name: string | null;
  privacyClass: ContainerPrivacyClass;
  accessOverride: "workspace" | null;
  isDirectMessage?: boolean;
  owner: ContainerOwner | null;
  activeGrants: number;
}

interface ContainersResponse {
  source: string;
  containers: ConnectorContainer[];
}

/** The connector's own noun for its containers — never say "container" to users. */
const SOURCE_NOUNS: Record<string, { singular: string; plural: string }> = {
  slack: { singular: "channel", plural: "channels" },
  github: { singular: "repository", plural: "repositories" },
  linear: { singular: "team", plural: "teams" },
  notion: { singular: "space", plural: "spaces" },
  confluence: { singular: "space", plural: "spaces" },
  jira: { singular: "project", plural: "projects" },
  trello: { singular: "board", plural: "boards" },
};

function nounsFor(source: string): { singular: string; plural: string } {
  return SOURCE_NOUNS[source] ?? { singular: "source", plural: "sources" };
}

function isWorkspaceVisible(container: ConnectorContainer): boolean {
  return container.privacyClass === "public" || container.accessOverride === "workspace";
}

/**
 * Human-readable effective access for a container: an admin override opens it to
 * everyone; otherwise private/shared/unknown are the connecting user plus any
 * desks granted access.
 */
function accessSummary(container: ConnectorContainer): string {
  if (isWorkspaceVisible(container)) {
    return "Everyone in the workspace";
  }
  const ownerName = container.owner?.displayName ?? container.owner?.email ?? "the connecting user";
  if (container.activeGrants > 0) {
    const desks = container.activeGrants === 1 ? "1 team" : `${container.activeGrants} teams`;
    return `${ownerName} + ${desks}`;
  }
  return `${ownerName} only`;
}

/**
 * The collapsed-state summary, e.g. "14 channels · 11 open to everyone ·
 * 3 private · 5 direct messages". DMs are counted separately — they are
 * always private and never part of the open/private tally.
 */
function summaryLine(
  channels: ConnectorContainer[],
  dms: ConnectorContainer[],
  nouns: { singular: string; plural: string },
): string {
  const parts: string[] = [];
  if (channels.length > 0) {
    const open = channels.filter(isWorkspaceVisible).length;
    const closed = channels.length - open;
    parts.push(`${channels.length} ${channels.length === 1 ? nouns.singular : nouns.plural}`);
    if (open > 0) parts.push(`${open} open to everyone`);
    if (closed > 0) parts.push(`${closed} private`);
  }
  if (dms.length > 0) {
    parts.push(`${dms.length} direct ${dms.length === 1 ? "message" : "messages"}`);
  }
  return parts.join(" · ");
}

/**
 * Which Teams a container's content reaches (YAG-986): every workspace
 * connection reaches every Team by default, so an open container is read by
 * every Team that did not hide the connection, and a dark one only by the
 * Teams it was granted to.
 */
export function readByLine(
  container: Pick<ConnectorContainer, "activeGrants">,
  open: boolean,
  teams: { total: number; hidden: number } | null,
): string {
  if (open) {
    if (!teams || teams.total === 0) return "read by every Team";
    const reading = teams.total - teams.hidden;
    if (reading === teams.total) return "read by every Team";
    if (reading === 0) return "read by no Team";
    return `read by every Team but ${teams.hidden}`;
  }
  if (container.activeGrants === 0) return "read by no Team";
  return container.activeGrants === 1
    ? "read by 1 Team"
    : `read by ${container.activeGrants} Teams`;
}

export function ConnectorContainersList({ source }: { source: string }) {
  const [containers, setContainers] = useState<ConnectorContainer[] | null>(null);
  const [error, setError] = useState<"forbidden" | "failed" | null>(null);
  const [teams, setTeams] = useState<{ total: number; hidden: number } | null>(null);
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<Set<string>>(new Set());
  // Last failed mutation's sanitized message — a 400/403 must be visible, not
  // a silently un-flipped button. Cleared on the next attempt.
  const [mutationError, setMutationError] = useState<string | null>(null);

  /**
   * Admin reclassification: set (or clear) the workspace access override on one
   * container. Optimistic-on-success — update just this row's `accessOverride`.
   * Reversible either way, so it's one tap with no confirm modal.
   */
  async function setOverride(container: ConnectorContainer, next: "workspace" | null) {
    setPending((p) => new Set(p).add(container.id));
    setMutationError(null);
    try {
      const res = await fetch(
        `/api/connections/${encodeURIComponent(source)}/containers/${encodeURIComponent(container.id)}`,
        {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ accessOverride: next }),
        },
      );
      if (res.ok) {
        setContainers((prev) =>
          prev
            ? prev.map((c) => (c.id === container.id ? { ...c, accessOverride: next } : c))
            : prev,
        );
      } else {
        setMutationError(await readJsonError(res));
      }
    } catch {
      setMutationError("Request failed — check your connection and try again.");
    } finally {
      setPending((p) => {
        const n = new Set(p);
        n.delete(container.id);
        return n;
      });
    }
  }

  useEffect(() => {
    let cancelled = false;
    setTeams(null);
    // Which Teams read this connection (YAG-986). A read that misses leaves
    // the fact at its default words and is reported, never an error of its own.
    fetchConnectionTeams(source)
      .then((body) => {
        if (cancelled) return;
        setTeams({
          total: body.teams.length,
          hidden: body.teams.filter((team) => team.hidden).length,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        captureUiError(err, {
          area: "Connections",
          operation: "connector_teams_read",
          severity: "warning",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [source]);

  useEffect(() => {
    let cancelled = false;
    setContainers(null);
    setError(null);
    fetch(`/api/connections/${encodeURIComponent(source)}/containers`)
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 403) {
          setError("forbidden");
          return;
        }
        if (!res.ok) {
          setError("failed");
          return;
        }
        const body = (await res.json()) as ContainersResponse;
        if (!cancelled) setContainers(body.containers);
      })
      .catch(() => {
        if (!cancelled) setError("failed");
      });
    return () => {
      cancelled = true;
    };
  }, [source]);

  const nouns = nounsFor(source);

  // Quiet by default: non-admins (403), empty lists, and the in-flight load all
  // render nothing — the list only appears once there is something to show.
  if (error === "forbidden") return null;
  if (error === "failed") {
    return (
      <p className="mt-2 text-xs text-[var(--text-tertiary)]">
        Couldn&apos;t load {nouns.singular} visibility.
      </p>
    );
  }
  if (containers === null || containers.length === 0) return null;

  const dms = containers.filter((c) => c.isDirectMessage === true);
  const channels = containers.filter((c) => c.isDirectMessage !== true);
  const headingNoun = nouns.singular.charAt(0).toUpperCase() + nouns.singular.slice(1);

  function renderRow(container: ConnectorContainer, options: { withControl: boolean }) {
    const badge = PRIVACY_BADGE[container.privacyClass];
    const overridden = container.accessOverride === "workspace";
    // A public container is already workspace-visible — nothing to open.
    const canOverride = options.withControl && container.privacyClass !== "public";
    const busy = pending.has(container.id);
    // DM names are stored self-describing ("DM · Jane Doe") for other
    // surfaces; under the "Direct messages" group the prefix is redundant.
    const displayName = (container.name ?? container.externalId).replace(/^DM · /, "");
    return (
      <li
        key={container.id}
        className="flex items-center justify-between gap-3 rounded-[var(--radius-row)] border border-[var(--print-border)] bg-[var(--print-bg)] shadow-[var(--shadow-print)] px-3 py-1.5"
      >
        <div className="min-w-0 flex items-center gap-2">
          <span className="truncate text-sm text-[var(--text-primary)]">{displayName}</span>
          {options.withControl ? (
            <Badge size="xs" variant={badge.variant}>
              {overridden ? "Workspace (override)" : badge.label}
            </Badge>
          ) : null}
          {container.activeGrants > 0 && (
            <Badge size="xs" variant="neutral">
              {container.activeGrants === 1 ? "1 team" : `${container.activeGrants} teams`}
            </Badge>
          )}
        </div>
        <div className="shrink-0 flex items-center gap-2">
          <span className="text-xs text-[var(--text-tertiary)] truncate">
            {accessSummary(container)}
          </span>
          {options.withControl && (
            <span
              className="text-xs [font-family:var(--font-fact)] text-[var(--text-tertiary)] whitespace-nowrap"
              data-testid={`container-read-by-${container.id}`}
            >
              {readByLine(container, isWorkspaceVisible(container), teams)}
            </span>
          )}
          {canOverride && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void setOverride(container, overridden ? null : "workspace")}
              data-testid={`container-override-${container.id}`}
              className="rounded-md border border-[var(--border)] px-2 py-0.5 text-xs text-[var(--text-secondary)] hover:bg-[rgba(var(--glass-rgb),0.68)] disabled:opacity-50"
            >
              {overridden ? "Revert to private" : "Open to workspace"}
            </button>
          )}
        </div>
      </li>
    );
  }

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="mt-3 rounded-lg border border-[var(--border)]"
      data-testid={`connector-containers-${source}`}
    >
      <CollapsibleTrigger
        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
        data-testid={`connector-containers-toggle-${source}`}
      >
        <span className="min-w-0 flex items-baseline gap-2">
          <span className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
            {headingNoun} visibility
          </span>
          <span className="truncate text-xs [font-family:var(--font-fact)] text-[var(--text-tertiary)]">
            {summaryLine(channels, dms, nouns)}
          </span>
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-[var(--text-tertiary)] transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-3 pb-3">
          {mutationError && (
            <p
              className="mb-1.5 text-xs text-[var(--error)]"
              data-testid="container-override-error"
            >
              {mutationError}
            </p>
          )}
          {channels.length > 0 ? (
            <ul className="flex flex-col gap-1.5">
              {channels.map((container) => renderRow(container, { withControl: true }))}
            </ul>
          ) : null}
          {dms.length > 0 ? (
            <div className={channels.length > 0 ? "mt-3" : undefined}>
              <p className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
                Direct messages
              </p>
              <p className="mt-0.5 text-xs text-[var(--text-tertiary)]">
                Always private — only visible to the person who connected{" "}
                {source === "slack" ? "Slack" : "this service"}.
              </p>
              <ul className="mt-1.5 flex flex-col gap-1.5">
                {dms.map((container) => renderRow(container, { withControl: false }))}
              </ul>
            </div>
          ) : null}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
